/**
 * Das Vier-Wochen-Raster (`kal-grid`, ≥ 768px) — vier Wochenblöcke
 * untereinander, je Block ein Kopf (`kal-woche-<iso_week>`) mit
 * `week_display` **und** `range_display` wörtlich vom Server, darunter sieben
 * gleich breite Tageszellen (`CalendarDayCell`, AK-457).
 */

import { CalendarDayCell } from '@/features/calendar/CalendarDayCell'
import type { CalendarEntryActions } from '@/features/calendar/CalendarEntryRow'
import type { CalendarWeek } from '@/features/calendar/api'

interface CalendarGridProps {
  readonly weeks: readonly CalendarWeek[]
  readonly expandedDates: ReadonlySet<string>
  readonly onToggleExpand: (date: string) => void
  readonly actions: CalendarEntryActions
  readonly onAddAppointment: (date: string) => void
  readonly onAddTask: (date: string) => void
}

export function CalendarGrid({
  weeks,
  expandedDates,
  onToggleExpand,
  actions,
  onAddAppointment,
  onAddTask,
}: CalendarGridProps) {
  return (
    <div data-testid="kal-grid" className="flex flex-col gap-3 px-(--page-x) py-3">
      {weeks.map((week) => (
        <div key={`${String(week.iso_year)}-${String(week.iso_week)}`} className="flex flex-col gap-1.5">
          <div data-testid={`kal-woche-${String(week.iso_week)}`} className="flex items-baseline gap-2 px-1">
            <span data-numeric className="text-body text-text">
              {week.week_display}
            </span>
            <span data-numeric className="text-label text-muted">
              {week.range_display}
            </span>
          </div>

          <div className="grid grid-cols-7 gap-1.5">
            {week.days.map((day) => (
              <CalendarDayCell
                key={day.on}
                day={day}
                isExpanded={expandedDates.has(day.on)}
                onToggleExpand={() => {
                  onToggleExpand(day.on)
                }}
                actions={actions}
                onAddAppointment={() => {
                  onAddAppointment(day.on)
                }}
                onAddTask={() => {
                  onAddTask(day.on)
                }}
              />
            ))}
          </div>
        </div>
      ))}
    </div>
  )
}
